import { useController, type Control, type FieldValues, type Path } from 'react-hook-form';
import { Stack, TextInput, NumberInput, Switch, Text, Divider, SimpleGrid } from '@mantine/core';
import { SUB_ACCOUNT_CONSTRAINTS } from '@/lib/constants/account';
import type { CustomercfgQuotaFeaturesApp } from '@/lib/types/Customer';

interface SubAccountFormFieldsProps<T extends FieldValues> {
  control: Control<T>;
  isCreate: boolean;
  masterApp: CustomercfgQuotaFeaturesApp;
  storageHelperText?: string;
  transferHelperText?: string;
  appLimitHelperText?: string;
  appLimitError?: string;
}

/**
 * Form fields for sub-account create/edit.
 * Uses GB for storage/transfer display (bytes conversion happens at submit boundary).
 */
export function SubAccountFormFields<T extends FieldValues>({
  control,
  isCreate,
  masterApp,
  storageHelperText,
  transferHelperText,
  appLimitHelperText,
  appLimitError,
}: SubAccountFormFieldsProps<T>) {
  return (
    <Stack gap="lg">
      <Stack gap="md">
        <Text fw={600} size="lg">General</Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <ControlledTextInput
            control={control}
            name={'label' as Path<T>}
            label="Label"
            maxLength={SUB_ACCOUNT_CONSTRAINTS.LABEL_MAX}
            required
          />
          {!isCreate && (
            <ControlledSwitch
              control={control}
              name={'isActive' as Path<T>}
              label="Account Status"
              description="Enable or disable this sub-account."
            />
          )}
        </SimpleGrid>
      </Stack>

      <Divider />

      <Stack gap="md">
        <Text fw={600} size="lg">Resource Limits</Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <ControlledNumberInput
            control={control}
            name={'storageLimitGB' as Path<T>}
            label="Storage Limit (GB)"
            description={storageHelperText ?? 'Leave empty for unlimited'}
            placeholder="Unlimited"
            min={0}
            decimalScale={1}
          />
          <ControlledNumberInput
            control={control}
            name={'transferLimitGB' as Path<T>}
            label="Transfer Limit (GB)"
            description={transferHelperText ?? 'Leave empty for unlimited'}
            placeholder="Unlimited"
            min={0}
            decimalScale={1}
          />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <ControlledNumberInput
            control={control}
            name={'appCfgLimit' as Path<T>}
            label="Maximum Apps"
            description={appLimitHelperText}
            error={appLimitError}
            min={0}
            decimalScale={0}
            required
            emptyValue={0}
          />
        </SimpleGrid>
      </Stack>

      <Divider />

      <Stack gap="md">
        <Text fw={600} size="lg">App Platforms</Text>
        <ControlledSwitch
          control={control}
          name={'allowWeb' as Path<T>}
          label="Web App"
          description="App support for Web"
          disabled={!masterApp.allowWeb}
        />
        {/* Other platforms are not in-use yet — uncomment when ready:
        <ControlledSwitch
          control={control}
          name={'allowAndroid' as Path<T>}
          label="Android"
          description="App support for Android"
          disabled={!masterApp.allowAndroid}
        />
        <ControlledSwitch
          control={control}
          name={'allowIos' as Path<T>}
          label="iOS"
          description="App support for iOS"
          disabled={!masterApp.allowIos}
        />
        <ControlledSwitch
          control={control}
          name={'allowRoku' as Path<T>}
          label="Roku"
          description="App support for Roku"
          disabled={!masterApp.allowRoku}
        />
        <ControlledSwitch
          control={control}
          name={'allowAppletv' as Path<T>}
          label="Apple TV"
          description="App support for Apple TV"
          disabled={!masterApp.allowAppletv}
        />
        <ControlledSwitch
          control={control}
          name={'allowFiretv' as Path<T>}
          label="Fire TV"
          description="App support for Fire TV"
          disabled={!masterApp.allowFiretv}
        />
        <ControlledSwitch
          control={control}
          name={'allowAndroidtv' as Path<T>}
          label="Android TV"
          description="App support for Android TV"
          disabled={!masterApp.allowAndroidtv}
        />
        */}
      </Stack>
    </Stack>
  );
}

// ── Controlled form primitives ──────────────────────────────────────

function ControlledTextInput<T extends FieldValues>({
  control,
  name,
  label,
  maxLength,
  required,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  maxLength?: number;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <TextInput
      label={label}
      value={field.value ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      maxLength={maxLength}
      withAsterisk={required}
    />
  );
}

function ControlledNumberInput<T extends FieldValues>({
  control,
  name,
  label,
  description,
  error: externalError,
  placeholder,
  min,
  decimalScale,
  required,
  emptyValue = null,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  description?: string;
  error?: string;
  placeholder?: string;
  min?: number;
  decimalScale?: number;
  required?: boolean;
  /** Value when the field is cleared. Defaults to null (unlimited). Use 0 for required numeric fields. */
  emptyValue?: number | null;
}) {
  const { field, fieldState } = useController({ name, control });
  return (
    <NumberInput
      label={label}
      description={description}
      value={field.value ?? ''}
      onChange={(val) => field.onChange(val === '' ? emptyValue : Number(val))}
      onBlur={field.onBlur}
      error={externalError || fieldState.error?.message}
      placeholder={placeholder}
      min={min}
      decimalScale={decimalScale}
      allowNegative={false}
      withAsterisk={required}
    />
  );
}

function ControlledSwitch<T extends FieldValues>({
  control,
  name,
  label,
  description,
  disabled,
}: {
  control: Control<T>;
  name: Path<T>;
  label: string;
  description?: string;
  disabled?: boolean;
}) {
  const { field } = useController({ name, control });
  return (
    <Switch
      label={label}
      description={description}
      checked={field.value ?? false}
      onChange={(e) => field.onChange(e.currentTarget.checked)}
      onBlur={field.onBlur}
      disabled={disabled}
    />
  );
}
